import { MPEGDecoder } from "mpg123-decoder"
import { interleave } from "../../ring-buffer/audioqueue"
import { MpegAudioIndex } from "./MpegAudioIndex"

const OUTPUT_CHANNELS = 2
const MIXER_SAMPLE_RATE = 48000

const YIELD_INTERVAL_FRAMES = 100

/**
 * Decodes an entire MP3 region into a 48 kHz stereo interleaved PCM buffer.
 *
 * The full sequential decode ensures the bit reservoir is always fully primed
 * so the output is identical to what a continuous playback from the start
 * would produce.  The returned buffer is suitable for wrapping in a
 * `PcmBufferRegionDecoder`.
 *
 * @param yieldToEventLoop  When true, yields to the event loop every
 *   {@link YIELD_INTERVAL_FRAMES} frames via `setTimeout(0)` to avoid
 *   blocking the UI.  Enable this when decoding on the main thread
 *   (e.g. from `RegionLoader`); leave false in a worker context.
 */
export async function decodeMp3ToPcmBuffer(
  index: MpegAudioIndex,
  yieldToEventLoop = false,
): Promise<Float32Array> {
  const resampleRatio = MIXER_SAMPLE_RATE / index.sampleRate

  const decoder = new MPEGDecoder({ enableGapless: false })
  await decoder.ready

  const chunks: Float32Array[] = []
  let totalSamples = 0

  for (let i = 0; i < index.frames.length; i++) {
    const frameData = index.getFrameData(i)
    const decoded = decoder.decodeFrame(frameData)

    if (decoded.errors.length > 0) {
      throw new Error(decoded.errors.map((error) => error.message).join(", "))
    }

    if (decoded.samplesDecoded === 0) {
      continue
    }

    const stereo = toStereoInterleaved(decoded.channelData, decoded.samplesDecoded)
    const resampled = resampleToMixerRate(stereo, resampleRatio)
    chunks.push(resampled)
    totalSamples += resampled.length

    if (yieldToEventLoop && i % YIELD_INTERVAL_FRAMES === 0 && i > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0))
    }
  }

  decoder.free()

  const buffer = new Float32Array(totalSamples)
  let offset = 0
  for (const chunk of chunks) {
    buffer.set(chunk, offset)
    offset += chunk.length
  }

  return buffer
}

function resampleToMixerRate(input: Float32Array, resampleRatio: number): Float32Array {
  if (resampleRatio === 1) {
    return input
  }

  const inputFrameCount = input.length / OUTPUT_CHANNELS
  const outputFrameCount = Math.round(inputFrameCount * resampleRatio)
  if (outputFrameCount === 0) {
    return new Float32Array(0)
  }

  const output = new Float32Array(outputFrameCount * OUTPUT_CHANNELS)
  const step = inputFrameCount / outputFrameCount

  for (let outFrame = 0; outFrame < outputFrameCount; outFrame++) {
    const srcPos = outFrame * step
    const baseFrame = Math.floor(srcPos)
    const fraction = srcPos - baseFrame
    const nextFrame = Math.min(baseFrame + 1, inputFrameCount - 1)

    for (let channel = 0; channel < OUTPUT_CHANNELS; channel++) {
      const a = input[baseFrame * OUTPUT_CHANNELS + channel]
      const b = input[nextFrame * OUTPUT_CHANNELS + channel]
      output[outFrame * OUTPUT_CHANNELS + channel] = a + (b - a) * fraction
    }
  }

  return output
}

function toStereoInterleaved(channelData: Float32Array[], frameCount: number): Float32Array {
  if (frameCount <= 0) {
    return new Float32Array(0)
  }

  const leftChannel = channelData[0].subarray(0, frameCount)
  const rightChannel = (channelData[1] ?? channelData[0]).subarray(0, frameCount)
  const output = new Float32Array(frameCount * OUTPUT_CHANNELS)
  interleave([leftChannel, rightChannel], output, frameCount)
  return output
}
